import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { RemoveCircleOutline, AddCircleOutline } from "@mui/icons-material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { PlayerRotationMinutes } from "../types";
import { getInitials } from "../QuarterGrid/utils";
import { baseColumns } from "./constants";

export default function Summary({
  players,
  loading,
  excludedPlayerIds,
  playerColorMap,
  onExclude,
  onInclude,
}: {
  players: PlayerRotationMinutes[];
  loading: boolean;
  excludedPlayerIds: Set<number>;
  playerColorMap: Record<number, string>;
  onExclude: (playerId: number) => void;
  onInclude: (playerId: number) => void;
}) {
  const theme = useTheme();

  const columns: GridColDef<PlayerRotationMinutes>[] = [
    {
      field: "actions",
      headerName: "",
      width: 30,
      minWidth: 30,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isExcluded = excludedPlayerIds.has(params.row.playerId);
        return (
          <IconButton
            size="small"
            onClick={() =>
              isExcluded
                ? onInclude(params.row.playerId)
                : onExclude(params.row.playerId)
            }
            disableFocusRipple
            sx={{
              p: 0,
              opacity: isExcluded ? 1 : 0.3,
              ".MuiDataGrid-row:hover &": { opacity: 1 },
            }}
          >
            {isExcluded ? (
              <AddCircleOutline fontSize="small" />
            ) : (
              <RemoveCircleOutline fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      ...baseColumns[0],
      renderCell: (params) => {
        const color = playerColorMap[params.row.playerId];
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ height: "100%" }}
          >
            <Typography
              sx={{
                width: 24,
                height: 24,
                borderRadius: 0.5,
                bgcolor: color,
                color: theme.palette.getContrastText(color),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: "bold",
                flexShrink: 0,
              }}
            >
              {getInitials(params.row.playerName)}
            </Typography>
            <Typography variant="body2">{params.row.playerName}</Typography>
          </Stack>
        );
      },
    },
    ...baseColumns.slice(1),
  ];

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 480 },
        minWidth: 300,
        "@media print": { width: 450, overflow: "hidden" },
      }}
    >
      <DataGridPro
        rows={players}
        columns={columns}
        getRowId={(row) => row.playerId}
        loading={loading}
        disableRowSelectionOnClick
        autoHeight
        hideFooter
        density="compact"
        getRowClassName={(params) =>
          excludedPlayerIds.has(params.row.playerId) ? "excluded" : ""
        }
        sx={{
          "& .excluded .MuiDataGrid-cell:not([data-field='actions'])": {
            opacity: 0.5,
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "@media print": {
            "& .MuiDataGrid-cell[data-field='actions'], & .MuiDataGrid-columnHeader[data-field='actions']":
              {
                display: "none",
              },
          },
        }}
      />
    </Box>
  );
}
